import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import Sidebar from "@/components/propgen/Sidebar";
import SimilarProjects from "@/components/propgen/SimilarProjects";
import { api, formatApiError } from "@/lib/propgen_api";
import { useAuth } from "@/lib/propgen_auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, Zap, Clock, Coins, Sparkles, Info, Layers, Plug, Palette, TrendingUp,
  AlertTriangle, TrendingDown, Check,
} from "lucide-react";

// ============================================================
// INTERNAL build-cost estimator. Never shared with clients.
// Uses "credits" as the internal unit of AI-assisted build effort.
// ============================================================
const DEFAULTS = {
  base_setup_credits: 30,          // Initial planning, scaffolding, auth, deploy setup
  credits_low: 8,                  // per low-complexity feature
  credits_medium: 18,              // per medium-complexity feature
  credits_high: 35,                // per high-complexity feature
  credits_integration: 20,         // per 3rd-party integration (Stripe, LLM, etc.)
  ai_feature_multiplier: 1.5,      // multiplier applied to category="ai" features
  design_low: 0.9,
  design_medium: 1.0,
  design_high: 1.25,
  polish_buffer_pct: 20,           // testing + iteration polish buffer
  credits_per_hour: 40,            // rough — 40 credits per active hour
  productive_hours_per_day: 3,     // realistic daily productive-work window
  band_low_pct: 15,                // range shown: mid × (1 - band) .. mid × (1 + band)
  band_high_pct: 25,
};

const FIELD_META = {
  base_setup_credits: { label: "Base Setup", icon: Sparkles, hint: "Auth, deploy, DB scaffolding" },
  credits_low: { label: "Credits · Low feature", icon: Layers, hint: "Trivial CRUD, static views" },
  credits_medium: { label: "Credits · Medium feature", icon: Layers, hint: "Standard business logic" },
  credits_high: { label: "Credits · High feature", icon: Layers, hint: "Complex flows, custom UI" },
  credits_integration: { label: "Per integration", icon: Plug, hint: "Stripe, LLM APIs, etc." },
  ai_feature_multiplier: { label: "AI feature ×", icon: Zap, hint: "For features tagged category=ai" },
  design_low: { label: "Design low ×", icon: Palette, hint: "Utility / internal tool aesthetic" },
  design_medium: { label: "Design medium ×", icon: Palette, hint: "Modern SaaS default" },
  design_high: { label: "Design high ×", icon: Palette, hint: "Custom/brand-heavy visuals" },
  polish_buffer_pct: { label: "Polish buffer %", icon: TrendingUp, hint: "Test iteration, bug fixes" },
  credits_per_hour: { label: "Credits / hour", icon: Clock, hint: "Credit burn rate" },
  productive_hours_per_day: { label: "Productive hrs / day", icon: Clock, hint: "Realistic focus window" },
  band_low_pct: { label: "Low band %", icon: TrendingUp, hint: "Confidence range floor" },
  band_high_pct: { label: "High band %", icon: TrendingUp, hint: "Confidence range ceiling" },
};

function computeEmergentEstimate(requirements, params) {
  const features = requirements?.features || [];
  const integrations = requirements?.integrations || [];
  const designComplexity = requirements?.design_complexity || "medium";
  const designMult = params[`design_${designComplexity}`] ?? 1.0;

  let featureCredits = 0;
  const featureRows = [];
  for (const f of features) {
    const perFeature = f.complexity === "high" ? params.credits_high
      : f.complexity === "low" ? params.credits_low
      : params.credits_medium;
    const aiMult = f.category === "ai" ? params.ai_feature_multiplier : 1.0;
    const credits = perFeature * aiMult;
    featureCredits += credits;
    featureRows.push({
      name: f.name || "Untitled",
      complexity: f.complexity || "medium",
      category: f.category || "core",
      credits: Math.round(credits),
    });
  }

  const integrationCredits = integrations.length * params.credits_integration;
  const subtotal = params.base_setup_credits + featureCredits + integrationCredits;
  const afterDesign = subtotal * designMult;
  const buffered = afterDesign * (1 + params.polish_buffer_pct / 100);
  const total = Math.round(buffered);

  const hours = total / Math.max(1, params.credits_per_hour);
  const days = hours / Math.max(1, params.productive_hours_per_day);

  const low = Math.round(total * (1 - params.band_low_pct / 100));
  const high = Math.round(total * (1 + params.band_high_pct / 100));
  const hoursLow = low / params.credits_per_hour;
  const hoursHigh = high / params.credits_per_hour;
  const daysLow = hoursLow / params.productive_hours_per_day;
  const daysHigh = hoursHigh / params.productive_hours_per_day;

  return {
    total,
    low,
    high,
    hours,
    days,
    hoursLow,
    hoursHigh,
    daysLow,
    daysHigh,
    designMult,
    breakdown: {
      base: params.base_setup_credits,
      features: Math.round(featureCredits),
      integrations: integrationCredits,
      designAdd: Math.round(afterDesign - subtotal),
      buffer: Math.round(buffered - afterDesign),
    },
    featureRows,
    integrationsCount: integrations.length,
  };
}

const fmt = (n) => Math.round(n).toLocaleString();
const fmt1 = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });

export default function EmergentEstimate() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [params, setParams] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [benchmarks, setBenchmarks] = useState(null);
  const [actualsInput, setActualsInput] = useState({ credits: "", hours: "" });
  const [savingActuals, setSavingActuals] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [proj, bench] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/benchmarks`).catch(() => ({ data: null })),
        ]);
        setProject(proj.data);
        setBenchmarks(bench.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const estimate = useMemo(() => {
    if (!project?.requirements) return null;
    return computeEmergentEstimate(project.requirements, params);
  }, [project, params]);

  // Access guard — non-privileged users get bounced back to the project
  if (user === null) return <Navigate to="/login" replace />;
  if (user && user.can_see_internal === false) return <Navigate to={`/superadmin/propgen/projects/${id}`} replace />;

  const reset = () => setParams(DEFAULTS);

  const loadLearned = () => {
    if (!benchmarks?.learned_params) return;
    setParams(benchmarks.learned_params);
    toast.success(`Loaded multipliers learned from ${benchmarks.count} past project${benchmarks.count === 1 ? "" : "s"}`);
  };

  const recordActuals = async () => {
    const c = Number(actualsInput.credits);
    const h = Number(actualsInput.hours);
    if (!c || !h) { toast.error("Enter both credits and hours"); return; }
    setSavingActuals(true);
    try {
      await api.post(`/projects/${id}/actuals`, { actual_credits: c, actual_hours: h });
      toast.success("Actuals recorded · will feed into team benchmarks");
      const { data: proj } = await api.get(`/projects/${id}`);
      const { data: bench } = await api.get(`/benchmarks`);
      setProject(proj);
      setBenchmarks(bench);
      setActualsInput({ credits: "", hours: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    setSavingActuals(false);
  };

  if (loading || !project) {
    return (
      <div className="flex min-h-screen">
        <Sidebar onNewProject={() => nav("/superadmin/propgen")} />
        <main className="flex-1 p-8 text-sm text-muted-foreground" data-testid="emergent-loading">Loading project…</main>
      </div>
    );
  }

  if (!project.requirements) {
    return (
      <div className="flex min-h-screen">
        <Sidebar onNewProject={() => nav("/superadmin/propgen")} />
        <main className="flex-1 p-8" data-testid="emergent-empty">
          <Link to={`/superadmin/propgen/projects/${id}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to project
          </Link>
          <div className="mt-8 border-2 border-dashed p-12 text-center max-w-xl mx-auto">
            <Zap className="w-8 h-8 mx-auto text-muted-foreground" />
            <div className="font-display font-bold mt-3 text-xl">No extracted scope yet</div>
            <p className="text-sm text-muted-foreground mt-2">Run AI extraction on the transcript first — the Emergent estimator uses the feature list to compute credits.</p>
          </div>
        </main>
      </div>
    );
  }

  const clientPrice = project.estimate?.external_price;
  const agencyHours = project.estimate?.total_hours;

  return (
    <div className="flex min-h-screen bg-background" data-testid="emergent-estimate-page">
      <Sidebar onNewProject={() => nav("/superadmin/propgen")} />
      <main className="flex-1 min-w-0">
        {/* Internal-only warning banner */}
        <div className="border-b bg-red-50 dark:bg-red-950/30 px-8 py-2 flex items-center gap-2 text-xs font-mono text-red-800 dark:text-red-200" data-testid="internal-banner">
          <AlertTriangle className="w-3.5 h-3.5" /> INTERNAL ONLY · DO NOT SHARE WITH CLIENT
        </div>

        {/* Header */}
        <div className="border-b bg-card">
          <div className="px-8 pt-6 pb-6">
            <div className="flex items-center gap-2 text-xs">
              <Link to="/superadmin/propgen" className="text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="emergent-back-all">
                <ArrowLeft className="w-3 h-3" /> All projects
              </Link>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
              <Link to={`/superadmin/propgen/projects/${id}`} className="text-muted-foreground hover:text-foreground" data-testid="emergent-back-project">
                {project.name}
              </Link>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
              <span className="text-foreground">Internal Build Cost</span>
            </div>
            <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="eyebrow flex items-center gap-2"><Zap className="w-3 h-3 text-[var(--peak-blue)]" /> Internal · AI-Assisted Build</div>
                <h1 className="font-display text-4xl font-black tracking-tighter mt-1" data-testid="emergent-title">
                  What will this cost us to ship?
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Credit + timeline forecast for building the extracted scope of <span className="font-medium text-foreground">{project.name}</span> in-house.
                  Tune multipliers to match your team&apos;s velocity.
                </p>
              </div>
              <button
                onClick={reset}
                data-testid="emergent-reset-btn"
                className="h-10 px-4 text-xs uppercase tracking-wider font-mono border-2 hover:bg-muted transition-colors"
              >
                Reset multipliers
              </button>
            </div>
          </div>
        </div>

        {estimate && (
          <div className="p-8 space-y-8 max-w-[1400px]">
            {/* Hero KPI cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 border bg-card" data-testid="emergent-hero">
              <div className="p-8 md:border-r border-b md:border-b-0 relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-[var(--peak-blue)]/5" />
                <div className="relative">
                  <div className="eyebrow flex items-center gap-1.5"><Coins className="w-3 h-3 text-[var(--peak-blue)]" /> Credits (mid)</div>
                  <div className="font-display text-6xl font-black tracking-tighter mt-2 text-[var(--peak-blue)]" data-testid="emergent-credits-mid">
                    {fmt(estimate.total)}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-1">
                    range {fmt(estimate.low)} – {fmt(estimate.high)}
                  </div>
                </div>
              </div>

              <div className="p-8 md:border-r border-b md:border-b-0">
                <div className="eyebrow flex items-center gap-1.5"><Clock className="w-3 h-3" /> Active build hours</div>
                <div className="font-display text-6xl font-black tracking-tighter mt-2 font-mono" data-testid="emergent-hours-mid">
                  {fmt1(estimate.hours)}h
                </div>
                <div className="text-xs font-mono text-muted-foreground mt-1">
                  range {fmt1(estimate.hoursLow)}h – {fmt1(estimate.hoursHigh)}h
                </div>
              </div>

              <div className="p-8 pattern-internal">
                <div className="eyebrow flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Wall-clock days</div>
                <div className="font-display text-6xl font-black tracking-tighter mt-2 font-mono" data-testid="emergent-days-mid">
                  {fmt1(estimate.days)}
                </div>
                <div className="text-xs font-mono text-muted-foreground mt-1">
                  range {fmt1(estimate.daysLow)} – {fmt1(estimate.daysHigh)} days
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-2">
                  @ {params.productive_hours_per_day}h focused work / day
                </div>
              </div>
            </div>

            {/* Value comparison strip */}
            {(clientPrice || agencyHours) && (
              <div className="border bg-card p-6" data-testid="emergent-comparison">
                <div className="eyebrow mb-3">Comparison · In-house build vs external agency estimate</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ComparisonCol
                    tag="EXTERNAL"
                    color="#111"
                    primary={clientPrice ? `$${fmt(clientPrice)}` : "—"}
                    primaryLabel="client-facing quote"
                    secondary={agencyHours ? `${fmt1(agencyHours)}h` : "—"}
                    secondaryLabel="quoted agency hours"
                    testid="cmp-traditional"
                  />
                  <ComparisonCol
                    tag="IN-HOUSE"
                    color="var(--peak-blue)"
                    primary={`${fmt(estimate.total)} credits`}
                    primaryLabel="mid estimate"
                    secondary={`${fmt1(estimate.hours)}h`}
                    secondaryLabel="active build time"
                    testid="cmp-emergent"
                  />
                  <div className="p-4 bg-[var(--peak-blue)]/5 border-2 border-[var(--peak-blue)]" data-testid="cmp-delta">
                    <div className="eyebrow text-[var(--peak-blue)]">Delta</div>
                    <div className="font-display text-3xl font-black tracking-tighter mt-1 text-[var(--peak-blue)]">
                      {agencyHours ? `${fmt1(agencyHours / Math.max(estimate.hours, 0.1))}×` : "—"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      faster than the quoted estimate
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Similar past proposals sanity check */}
            <SimilarProjects projectId={id} limit={3} />

            {/* Learned benchmarks + actuals recorder */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="emergent-history">
              {/* Team benchmarks */}
              <div className="border bg-card p-5">
                <div className="eyebrow flex items-center gap-2 mb-3">
                  <TrendingDown className="w-3 h-3 text-[var(--peak-blue)]" /> Learned from your team&apos;s history
                </div>
                {benchmarks && benchmarks.count > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Delivered</div>
                        <div className="font-display text-2xl font-black tracking-tighter mt-1">{benchmarks.count}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">projects</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Avg burn</div>
                        <div className="font-display text-2xl font-black tracking-tighter mt-1 font-mono">{benchmarks.avg_credits_per_hour}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">credits / hour</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Median</div>
                        <div className="font-display text-2xl font-black tracking-tighter mt-1 font-mono">{benchmarks.median_credits_per_hour}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">credits / hour</div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mb-3">
                      Your team&apos;s actual burn rate is <b className="text-foreground">{benchmarks.avg_credits_per_hour} cr/h</b>. Default assumes <b>{DEFAULTS.credits_per_hour} cr/h</b>. Load learned rate to re-forecast.
                    </div>
                    <Button
                      onClick={loadLearned}
                      data-testid="emergent-load-learned"
                      disabled={params.credits_per_hour === benchmarks.avg_credits_per_hour}
                      className="h-9 bg-black hover:bg-[var(--peak-blue)] text-white text-xs uppercase font-display font-bold tracking-wide transition-colors"
                    >
                      <TrendingDown className="w-3.5 h-3.5 mr-2" />
                      {params.credits_per_hour === benchmarks.avg_credits_per_hour ? "Learned rate applied" : "Load learned multipliers"}
                    </Button>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground italic">
                    No completed projects yet. Once you record actuals on delivered projects, your team&apos;s real burn rate will auto-tune the estimates here.
                  </div>
                )}
              </div>

              {/* Actuals recorder */}
              <div className={`border bg-card p-5 ${project.actuals ? "" : "border-dashed"}`} data-testid="emergent-actuals">
                <div className="eyebrow flex items-center gap-2 mb-3">
                  <Check className="w-3 h-3 text-emerald-600" /> Record actuals for this project
                </div>
                {project.actuals ? (
                  <div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Actual credits</div>
                        <div className="font-display text-2xl font-black tracking-tighter mt-1 font-mono">{fmt(project.actuals.actual_credits)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Actual hours</div>
                        <div className="font-display text-2xl font-black tracking-tighter mt-1 font-mono">{fmt1(project.actuals.actual_hours)}h</div>
                      </div>
                    </div>
                    {project.actuals.forecast_at_completion && (
                      <div className="text-xs text-muted-foreground border-t pt-2">
                        Forecast at completion was <b className="text-foreground">{project.actuals.forecast_at_completion.total} credits</b> · Delta <b className={project.actuals.actual_credits > project.actuals.forecast_at_completion.total ? "text-red-700" : "text-emerald-700"}>
                          {project.actuals.actual_credits > project.actuals.forecast_at_completion.total ? "+" : ""}
                          {fmt(project.actuals.actual_credits - project.actuals.forecast_at_completion.total)}
                        </b> ({fmt1(((project.actuals.actual_credits / project.actuals.forecast_at_completion.total) - 1) * 100)}%)
                      </div>
                    )}
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-2">
                      Recorded {new Date(project.actuals.recorded_at).toLocaleDateString()} by {project.actuals.recorded_by}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-xs text-muted-foreground">
                      When this project is delivered, record what it actually cost. This trains the team benchmark on the left.
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Actual credits used</label>
                        <Input
                          type="number"
                          step={1}
                          value={actualsInput.credits}
                          onChange={(e) => setActualsInput({ ...actualsInput, credits: e.target.value })}
                          data-testid="emergent-actuals-credits"
                          placeholder="e.g. 245"
                          className="h-9 border-2 mt-1 font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Actual build hours</label>
                        <Input
                          type="number"
                          step={0.1}
                          value={actualsInput.hours}
                          onChange={(e) => setActualsInput({ ...actualsInput, hours: e.target.value })}
                          data-testid="emergent-actuals-hours"
                          placeholder="e.g. 6.1"
                          className="h-9 border-2 mt-1 font-mono text-sm"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={recordActuals}
                      disabled={savingActuals || !actualsInput.credits || !actualsInput.hours}
                      data-testid="emergent-actuals-save"
                      className="h-9 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white text-xs uppercase font-display font-bold tracking-wide w-full"
                    >
                      {savingActuals ? "Saving…" : "Record actuals · mark delivered"}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Breakdown + Params grid */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8">
              {/* Breakdown */}
              <div className="space-y-6">
                <div className="border bg-card" data-testid="emergent-breakdown">
                  <div className="px-4 py-3 border-b bg-muted/30 flex items-center gap-2">
                    <div className="eyebrow">Credit breakdown</div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground ml-auto">
                      base · features · integrations · design · polish
                    </span>
                  </div>
                  <div className="divide-y">
                    <BreakdownRow icon={Sparkles} label="Base setup" hint="Auth, project scaffolding, deploy config, DB init" credits={estimate.breakdown.base} total={estimate.total} testid="bd-base" />
                    <BreakdownRow icon={Layers} label={`Features (${estimate.featureRows.length})`} hint="Per-feature credits after AI multiplier" credits={estimate.breakdown.features} total={estimate.total} testid="bd-features" />
                    <BreakdownRow icon={Plug} label={`Integrations (${estimate.integrationsCount})`} hint={`${params.credits_integration} credits each`} credits={estimate.breakdown.integrations} total={estimate.total} testid="bd-integrations" />
                    <BreakdownRow icon={Palette} label={`Design complexity (${project.requirements.design_complexity || "medium"})`} hint={`× ${estimate.designMult} applied to base+features+integrations`} credits={estimate.breakdown.designAdd} total={estimate.total} testid="bd-design" />
                    <BreakdownRow icon={TrendingUp} label="Polish buffer" hint={`${params.polish_buffer_pct}% test + iteration reserve`} credits={estimate.breakdown.buffer} total={estimate.total} testid="bd-buffer" />
                    <div className="px-4 py-3 flex items-center justify-between bg-[var(--peak-blue)]/5">
                      <div className="font-display font-bold uppercase text-sm tracking-wide">Total (mid)</div>
                      <div className="font-mono font-bold text-[var(--peak-blue)]">{fmt(estimate.total)} credits</div>
                    </div>
                  </div>
                </div>

                {/* Per-feature table */}
                <div className="border bg-card" data-testid="emergent-feature-table">
                  <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
                    <div className="eyebrow">Per feature ({estimate.featureRows.length})</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                      pre-design, pre-buffer
                    </div>
                  </div>
                  <div className="grid grid-cols-[1.6fr_120px_120px_100px] items-center px-4 py-2 border-b bg-muted/50 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                    <div>Feature</div><div>Category</div><div>Complexity</div><div className="text-right">Credits</div>
                  </div>
                  {estimate.featureRows.length === 0 && (
                    <div className="p-6 text-sm text-muted-foreground italic text-center">No features extracted from the transcript.</div>
                  )}
                  {estimate.featureRows.map((r, i) => (
                    <div key={i} className="grid grid-cols-[1.6fr_120px_120px_100px] items-center px-4 py-2 border-b last:border-b-0 text-sm" data-testid={`emergent-feature-row-${i}`}>
                      <div className="truncate">{r.name}</div>
                      <div className="font-mono text-xs uppercase text-muted-foreground">{r.category}{r.category === "ai" && <span className="ml-1 text-[var(--peak-blue)]">×{params.ai_feature_multiplier}</span>}</div>
                      <div className="font-mono text-xs uppercase">{r.complexity}</div>
                      <div className="font-mono text-right">{r.credits}</div>
                    </div>
                  ))}
                </div>

                {/* Disclaimer */}
                <div className="border-l-2 border-neutral-400 bg-muted/30 px-4 py-3 flex items-start gap-2 text-xs text-muted-foreground" data-testid="emergent-disclaimer">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    Internal forecast — never surfaced to clients. Actual credit consumption varies with iteration count, transcript ambiguity, and third-party integration friction. Adjust the multipliers on the right to match your team&apos;s empirical velocity.
                  </div>
                </div>
              </div>

              {/* Params sidebar */}
              <div className="border bg-card sticky top-4 h-fit" data-testid="emergent-params">
                <div className="px-4 py-3 border-b bg-muted/30">
                  <div className="eyebrow">Multipliers</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Live-recomputes above on change.</div>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  {Object.entries(FIELD_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    return (
                      <div key={key} className="col-span-2 sm:col-span-1">
                        <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                          <Icon className="w-3 h-3" /> {meta.label}
                        </label>
                        <Input
                          type="number"
                          step={key.includes("multiplier") || key.startsWith("design_") ? 0.05 : 1}
                          value={params[key]}
                          onChange={(e) => setParams({ ...params, [key]: Number(e.target.value) })}
                          data-testid={`emergent-param-${key}`}
                          className="h-9 border-2 mt-1 font-mono text-xs"
                        />
                        <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{meta.hint}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ComparisonCol({ tag, color, primary, primaryLabel, secondary, secondaryLabel, testid }) {
  return (
    <div className="p-4 border" data-testid={testid}>
      <div className="inline-block text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 mb-2" style={{ background: color, color: "#fff" }}>
        {tag}
      </div>
      <div className="font-display text-3xl font-black tracking-tighter" style={{ color }}>{primary}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{primaryLabel}</div>
      <div className="text-sm font-mono mt-3 pt-3 border-t">{secondary}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{secondaryLabel}</div>
    </div>
  );
}

function BreakdownRow({ icon: Icon, label, hint, credits, total, testid }) {
  const pct = total > 0 ? (credits / total) * 100 : 0;
  return (
    <div className="px-4 py-3 flex items-center gap-4" data-testid={testid}>
      <div className="w-6 flex justify-center"><Icon className="w-4 h-4 text-muted-foreground" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-muted-foreground">{hint}</div>
        <div className="mt-2 h-1 bg-neutral-200 dark:bg-neutral-800 relative">
          <div className="absolute inset-y-0 left-0 bg-[var(--peak-blue)]" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
        </div>
      </div>
      <div className="text-right shrink-0 w-24">
        <div className="font-mono font-bold text-sm">{credits}</div>
        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{fmt1(pct)}%</div>
      </div>
    </div>
  );
}
